import { useEffect, useMemo, useState } from 'react';
import { describeError } from '@pc/core';
import { Card, Field, Input, Rule } from '@pc/ui';
import type {
  PublicProposalSlotAcceptance,
  PublicProposalSlotContext,
} from '@pc/modules-orders/slots';
import { usePublicProposalContracts } from '../queries';
import { ContractMarkdown } from '../components/ContractMarkdown';

interface RowState {
  acceptorName: string;
  consent: boolean;
}

/**
 * Renders the list of attached contracts on the client-facing public
 * approval page. Captures ESIGN (USA) compliant acceptances:
 *
 *   * Intent to sign: distinct typed full name per contract.
 *   * Consent to electronic signature: separate required checkbox per
 *     contract (UETA §5(b) "agreement to conduct transaction
 *     electronically").
 *   * Attribution: IP + user agent are captured server-side by the
 *     notify-proposal edge function on submit.
 *   * Document integrity: the SHA-256 hash of the body_snapshot is
 *     persisted alongside the acceptance so any later mutation of the
 *     row can be detected.
 *
 * This component does NOT submit the acceptances itself. It bubbles
 * them up to the host PublicProposalPage via onAcceptancesChange so
 * approval + acceptance happen in a single atomic edge-function call.
 */
export function PublicContractsBlock(props: PublicProposalSlotContext) {
  const contractsQ = usePublicProposalContracts(props.token);
  const [rows, setRows] = useState<Record<string, RowState>>({});

  // Stabilize the contracts reference so downstream effects don't refire
  // on every render. `contractsQ.data` is a new array reference from the
  // query client only when the data actually changes; defaulting in a
  // memo keeps `[]` stable across renders too.
  const contracts = useMemo(() => contractsQ.data ?? [], [contractsQ.data]);

  // Initialize a row state per contract whenever the list refreshes.
  useEffect(() => {
    setRows((prev) => {
      const next: Record<string, RowState> = {};
      for (const c of contracts) {
        next[c.id] = prev[c.id] ?? { acceptorName: '', consent: false };
      }
      return next;
    });
  }, [contracts]);

  const { acceptances, valid } = useMemo(() => {
    const acc: PublicProposalSlotAcceptance[] = [];
    let v = true;
    for (const c of contracts) {
      const row = rows[c.id];
      if (c.accepted) continue; // already signed in a prior visit
      if (!c.require_signature) continue;
      if (!row || !row.acceptorName.trim() || !row.consent) {
        v = false;
        continue;
      }
      acc.push({
        proposalContractId: c.id,
        acceptorName: row.acceptorName.trim(),
        signatureText: row.acceptorName.trim(),
        consentElectronicSignature: row.consent,
      });
    }
    return { acceptances: acc, valid: v };
  }, [contracts, rows]);

  useEffect(() => {
    props.onAcceptancesChange?.(acceptances);
  }, [acceptances, props]);

  useEffect(() => {
    props.onValidityChange?.(valid);
  }, [valid, props]);

  if (contractsQ.isLoading) {
    return (
      <Card title="Contracts">
        <p className="smallcap">Loading contracts</p>
      </Card>
    );
  }
  if (contractsQ.error) {
    return (
      <Card title="Contracts">
        <p className="text-sm text-clay">{describeError(contractsQ.error)}</p>
      </Card>
    );
  }
  if (contracts.length === 0) return null;

  return (
    <Card
      title={
        contracts.length === 1 ? 'Contract to sign' : `Contracts to sign (${contracts.length})`
      }
    >
      <p className="text-sm text-ink-2 mb-4">
        Each contract below must be reviewed and signed before you can approve the proposal.
        Your typed full name, IP address, browser, and the moment of signing are recorded as a
        legally enforceable electronic signature under the U.S. <em>ESIGN Act</em> and{' '}
        <em>UETA</em>.
      </p>
      <ul className="flex flex-col">
        {contracts.map((c, i) => {
          const row = rows[c.id] ?? { acceptorName: '', consent: false };
          return (
            <li key={c.id}>
              {i > 0 ? <Rule className="my-4" /> : null}
              <div className="flex flex-col gap-3">
                <div className="flex items-baseline justify-between gap-4">
                  <h4 className="display text-base">{c.title}</h4>
                  {c.accepted ? (
                    <span className="text-xs text-olive">
                      Signed{c.accepted_at ? ` ${new Date(c.accepted_at).toLocaleDateString()}` : ''}
                      {c.acceptor_name ? ` by ${c.acceptor_name}` : ''}
                    </span>
                  ) : (
                    <span className="text-xs text-clay">Required</span>
                  )}
                </div>

                <details className="text-sm group">
                  <summary className="cursor-pointer text-muted underline hover:text-ink select-none">
                    Read the full contract
                  </summary>
                  <div
                    className="mt-3 max-h-[28rem] overflow-y-auto border border-rule p-4 bg-paper text-ink-2 text-sm leading-relaxed"
                    aria-label={`Body of contract ${c.title}`}
                  >
                    <ContractMarkdown source={c.body_md} />
                  </div>
                  <p className="text-[10px] text-muted tabular mt-2">
                    Document SHA-256 {c.body_sha256.slice(0, 16)}…
                  </p>
                </details>

                {!c.accepted ? (
                  <div className="flex flex-col gap-3 border border-rule p-4 bg-paper-2">
                    <Field label="Type your full legal name as your signature">
                      <Input
                        value={row.acceptorName}
                        onChange={(e) =>
                          setRows((p) => ({
                            ...p,
                            [c.id]: { ...row, acceptorName: e.target.value },
                          }))
                        }
                        placeholder="Jane A. Doe"
                        autoComplete="name"
                      />
                    </Field>
                    <label className="flex items-start gap-2 text-sm text-ink-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={row.consent}
                        onChange={(e) =>
                          setRows((p) => ({
                            ...p,
                            [c.id]: { ...row, consent: e.target.checked },
                          }))
                        }
                        className="mt-0.5"
                      />
                      <span>
                        I agree to use electronic records and electronic signatures for this
                        transaction. I understand my typed name above is my legally binding
                        signature on this document and that PeptideClients will retain a copy
                        of the signed document on my behalf.
                      </span>
                    </label>
                  </div>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
